/**
 * <m-inline-search> — self-contained live predictive search for the header.
 * Uses Shopify's Predictive Search API (/search/suggest.json) and renders a
 * results dropdown directly under the inline search box. Independent of the
 * theme's popup <predictive-search> component.
 */
if (!customElements.get('m-inline-search')) {
  class MInlineSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('[data-inline-search-input]');
      this.results = this.querySelector('[data-inline-search-results]');
      this.limit = parseInt(this.dataset.limit || '6', 10);
      this.minChars = 2;
      this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.i18n = {
        viewAll: this.dataset.viewAll || 'View all results',
        noResults: this.dataset.noResults || 'No results',
      };
      this.controller = null;
      this.onInput = this.debounce(this.search.bind(this), 250);
      this.onDocClick = (e) => { if (!this.contains(e.target)) this.close(); };
      this.bind();
    }

    bind() {
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', () => { if (this.results.children.length) this.open(); });
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { this.close(); this.input.blur(); }
      });
      document.addEventListener('click', this.onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    debounce(fn, delay) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), delay); };
    }

    open() { this.results.classList.remove('m:hidden'); this.setAttribute('open', ''); }
    close() { this.results.classList.add('m:hidden'); this.removeAttribute('open'); }

    async search() {
      const q = this.input.value.trim();
      if (q.length < this.minChars) { this.results.innerHTML = ''; this.close(); return; }

      const params = new URLSearchParams();
      params.set('q', q);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', String(this.limit));
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,variants.barcode,vendor');

      try {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const res = await fetch(`${this.root}search/suggest.json?${params.toString()}`, { signal: this.controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        this.render(products, q);
      } catch (e) {
        if (e.name !== 'AbortError') console.error('[inline-search]', e);
      }
    }

    render(products, q) {
      if (!products.length) {
        this.results.innerHTML = `<p class="m-inline-search__empty">${this.i18n.noResults} &ldquo;${this.escape(q)}&rdquo;</p>`;
        this.open();
        return;
      }

      const rows = products.map((p) => {
        const raw = (p.featured_image && p.featured_image.url) || (typeof p.image === 'string' ? p.image : '') || '';
        const src = raw ? (raw.indexOf('?') > -1 ? `${raw}&width=120` : `${raw}?width=120`) : '';
        const thumb = src
          ? `<img class="m-inline-search__thumb" src="${src}" alt="" width="48" height="48" loading="lazy">`
          : '<span class="m-inline-search__thumb m-inline-search__thumb--empty"></span>';
        const vendor = p.vendor ? `<span class="m-inline-search__vendor">${this.escape(p.vendor)}</span>` : '';
        return `<a class="m-inline-search__item" href="${p.url}" role="option">
          ${thumb}
          <span class="m-inline-search__info">
            <span class="m-inline-search__title">${this.escape(p.title)}</span>
            ${vendor}
          </span>
        </a>`;
      }).join('');

      this.results.innerHTML = rows + `<a class="m-inline-search__all" href="${this.searchPageUrl(q)}">${this.i18n.viewAll}</a>`;
      this.open();
    }

    // Build a full-search URL that matches the theme's own search form options,
    // so the results page uses the same prefix + field matching as this dropdown.
    searchPageUrl(q) {
      const p = new URLSearchParams();
      p.set('q', q);
      p.set('options[prefix]', 'last');
      p.set('options[unavailable_products]', 'last');
      p.set('options[fields]', 'title,vendor,product_type,variants.title,variants.sku,variants.barcode');
      return `${this.root}search?${p.toString()}`;
    }

    escape(str) {
      const d = document.createElement('div');
      d.textContent = str == null ? '' : String(str);
      return d.innerHTML;
    }
  }

  customElements.define('m-inline-search', MInlineSearch);
}
